import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface SocketMessage {
  event: string
  data: any
  timestamp: number
}

interface SocketState {
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  lastMessage: SocketMessage | null
  messageHistory: SocketMessage[]
  reconnectAttempts: number
  subscriptions: string[]
}

const initialState: SocketState = {
  isConnected: false,
  isConnecting: false,
  error: null,
  lastMessage: null,
  messageHistory: [],
  reconnectAttempts: 0,
  subscriptions: [],
}

const socketSlice = createSlice({
  name: 'socket',
  initialState,
  reducers: {
    connectSocket: (state) => {
      state.isConnecting = true
      state.error = null
    },
    socketConnected: (state) => {
      state.isConnected = true
      state.isConnecting = false
      state.reconnectAttempts = 0
      state.error = null
    },
    socketDisconnected: (state) => {
      state.isConnected = false
      state.isConnecting = false
    },
    socketError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isConnecting = false
    },
    socketReconnecting: (state) => {
      state.isConnecting = true
      state.reconnectAttempts += 1
    },
    receiveMessage: (state, action: PayloadAction<Omit<SocketMessage, 'timestamp'>>) => {
      const message: SocketMessage = {
        ...action.payload,
        timestamp: Date.now(),
      }
      state.lastMessage = message
      state.messageHistory.unshift(message)
      
      // Keep only last 100 messages
      if (state.messageHistory.length > 100) {
        state.messageHistory = state.messageHistory.slice(0, 100)
      }
    },
    subscribe: (state, action: PayloadAction<string>) => {
      if (!state.subscriptions.includes(action.payload)) {
        state.subscriptions.push(action.payload)
      }
    },
    unsubscribe: (state, action: PayloadAction<string>) => {
      state.subscriptions = state.subscriptions.filter(s => s !== action.payload)
    },
    clearMessages: (state) => {
      state.messageHistory = []
      state.lastMessage = null
    },
    resetSocket: () => {
      return initialState
    },
  },
})

export const {
  connectSocket,
  socketConnected,
  socketDisconnected,
  socketError,
  socketReconnecting,
  receiveMessage,
  subscribe,
  unsubscribe,
  clearMessages,
  resetSocket,
} = socketSlice.actions

export default socketSlice.reducer